import React, { useState, useEffect, useRef } from 'react';
import { Button, Space, message, Tag, Modal, Form, Input, InputNumber, Select } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import {
  getHuifuAccountList,
  addHuifuAccount,
  updateHuifuAccount,
  deleteHuifuAccount,
  getUsers,
  downloadHuifuAccountTemplate,
  batchImportHuifuAccounts,
} from '../../api';
import ListToolbar from '../../components/ListToolbar';
import { exportToCSV } from '../../utils/exportUtils';
import PageTitle from '../../components/PageTitle';
import ResizableTable from '../../components/ResizableTable';
import { showBatchImportResult, showBatchImportHttpError } from '../../utils/batchImportModal';

const HuifuAccountList = () => {
  const [accountData, setAccountData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [displayColumns, setDisplayColumns] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [form] = Form.useForm();
  const [userList, setUserList] = useState([]);
  const fileInputRef = useRef(null);

  // 获取汇付账号列表
  const fetchData = async (search = '', page = 1, pageSize = 20) => {
    setLoading(true);
    try {
      const res = await getHuifuAccountList(search, page, pageSize);
      if (res?.success && Array.isArray(res.data)) {
        setAccountData(res.data);
        setPagination({
          current: res.page || page,
          pageSize: res.page_size || pageSize,
          total: res.total || 0,
        });
        message.success(`加载成功 ${res.data.length} 条数据`);
      } else {
        setAccountData([]);
        message.error('数据格式错误');
      }
    } catch (err) {
      console.error('加载汇付账号失败：', err);
      message.error('加载汇付账号失败，请检查接口连接');
      setAccountData([]);
    } finally {
      setLoading(false);
    }
  };

  // 加载用户列表
  const loadUserList = async () => {
    try {
      const res = await getUsers();
      const users = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
      setUserList(users);
    } catch (err) {
      console.error('加载用户列表失败：', err);
    }
  };

  // 初始化加载
  useEffect(() => {
    fetchData();
    loadUserList();
  }, []);

  // 搜索处理
  const handleSearch = () => {
    fetchData(searchText, 1, pagination.pageSize);
  };

  // 重置搜索
  const handleReset = () => {
    setSearchText('');
    fetchData('', 1, pagination.pageSize);
  };

  // 刷新数据
  const handleRefresh = () => {
    fetchData(searchText, pagination.current, pagination.pageSize);
  };

  // 导出数据
  const handleExport = () => {
    const exportData = accountData.map(account => ({
      'ID': account.id,
      '用户ID': account.user_id,
      '真实姓名': account.real_name || '-',
      '手机号': account.phone || '-',
      '身份证号': account.id_card || '-',
      '证件有效期类型': account.cert_validity_type === 1 ? '长期有效' : '非长期有效',
      '证件有效期开始日期': account.cert_begin_date || '-',
      '证件有效期截止日期': account.cert_end_date || '-',
      '汇付ID': account.huifu_id || '-',
      '状态': account.status === 1 ? '正常' : '禁用',
      '创建时间': account.created_at || '-',
      '更新时间': account.updated_at || '-',
    }));

    if (exportToCSV(exportData, '汇付账号列表')) {
      message.success(`成功导出 ${exportData.length} 条数据`);
    } else {
      message.error('导出数据失败，请重试');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      await downloadHuifuAccountTemplate();
      message.success('模板下载成功');
    } catch (error) {
      console.error('下载模板失败：', error);
      message.error('下载模板失败，请重试');
    }
  };

  const handleBatchImport = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      message.error('请选择 CSV 文件');
      return;
    }
    try {
      setLoading(true);
      const res = await batchImportHuifuAccounts(file);
      showBatchImportResult(res, { entityName: '汇付账号' });
      if (res?.success) {
        fetchData(searchText, pagination.current, pagination.pageSize);
      }
    } catch (error) {
      console.error('批量导入失败：', error);
      showBatchImportHttpError(error);
    } finally {
      setLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // 分页变化处理
  const handleTableChange = (newPagination) => {
    fetchData(searchText, newPagination.current, newPagination.pageSize);
  };

  // 打开新增/编辑弹窗
  const showModal = (account = null) => {
    setEditingAccount(account);
    if (account) {
      form.setFieldsValue({
        user_id: account.user_id,
        real_name: account.real_name,
        phone: account.phone,
        id_card: account.id_card,
        cert_validity_type: account.cert_validity_type,
        cert_begin_date: account.cert_begin_date,
        cert_end_date: account.cert_end_date,
        huifu_id: account.huifu_id,
        status: account.status,
      });
    } else {
      form.resetFields();
    }
    setIsModalVisible(true);
  };

  // 提交表单
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      // 兜底：必填字段防漏（校验已做，但再防御一次）
      if (!values.huifu_id) return message.error('请填写汇付ID');
      if (!values.real_name) return message.error('请填写真实姓名');
      if (!values.user_id && !editingAccount) return message.error('请选择用户');
      setLoading(true);

      if (editingAccount) {
        // 编辑
        const res = await updateHuifuAccount(editingAccount.id, values);
        if (res?.success) {
          message.success('编辑汇付账号成功');
          fetchData(searchText, pagination.current, pagination.pageSize);
        } else {
          message.error('编辑汇付账号失败：' + (res?.msg || '后端返回异常'));
        }
      } else {
        // 新增
        const res = await addHuifuAccount(values);
        if (res?.success) {
          message.success('新增汇付账号成功');
          fetchData(searchText, pagination.current, pagination.pageSize);
        } else {
          message.error('新增汇付账号失败：' + (res?.msg || '后端返回异常'));
        }
      }
      setIsModalVisible(false);
    } catch (err) {
      console.error('提交失败：', err);
      if (err.errorFields) {
        message.error('请检查输入的数据');
      } else {
        message.error('提交失败，请检查输入或接口连接');
      }
    } finally {
      setLoading(false);
    }
  };

  // 删除账号
  const handleDelete = async (id) => {
    if (!window.confirm('确定要删除该汇付账号吗？删除后不可恢复！')) {
      return;
    }

    try {
      setLoading(true);
      const res = await deleteHuifuAccount(id);
      if (res?.success) {
        message.success('删除汇付账号成功');
        fetchData(searchText, pagination.current, pagination.pageSize);
      } else {
        message.error('删除汇付账号失败：' + (res?.msg || '后端返回异常'));
      }
    } catch (err) {
      console.error('删除失败：', err);
      const errMsg = err.response?.data?.msg || err.message || '删除汇付账号失败：网络/接口问题';
      message.error(errMsg);
    } finally {
      setLoading(false);
    }
  };

  // 原始表格列配置
  const originalColumns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
    { title: '用户ID', dataIndex: 'user_id', key: 'user_id', width: 100 },
    { title: '真实姓名', dataIndex: 'real_name', key: 'real_name', width: 120 },
    { title: '手机号', dataIndex: 'phone', key: 'phone', width: 130 },
    { 
      title: '身份证号', 
      dataIndex: 'id_card', 
      key: 'id_card', 
      width: 180,
      render: (text) => {
        if (!text) return '-';
        // 只显示前6位和后4位，中间用*代替
        if (text.length > 10) {
          return `${text.substring(0, 6)}${'*'.repeat(text.length - 10)}${text.substring(text.length - 4)}`;
        }
        return text;
      }
    },
    { 
      title: '证件有效期类型', 
      dataIndex: 'cert_validity_type', 
      key: 'cert_validity_type', 
      width: 140,
      render: (type) => type === 1 ? '长期有效' : '非长期有效'
    },
    { title: '证件有效期开始日期', dataIndex: 'cert_begin_date', key: 'cert_begin_date', width: 160 },
    { title: '证件有效期截止日期', dataIndex: 'cert_end_date', key: 'cert_end_date', width: 160 },
    { title: '汇付ID', dataIndex: 'huifu_id', key: 'huifu_id', width: 150 },
    { 
      title: '状态', 
      dataIndex: 'status', 
      key: 'status', 
      width: 80,
      render: (status) => (
        <Tag color={status === 1 ? 'green' : 'red'}>
          {status === 1 ? '正常' : '禁用'}
        </Tag>
      )
    },
    { title: '创建时间', dataIndex: 'created_at', key: 'created_at', width: 160 },
    { title: '更新时间', dataIndex: 'updated_at', key: 'updated_at', width: 160 },
    {
      title: '操作',
      key: 'action',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space size="middle">
          <Button icon={<EditOutlined />} size="small" onClick={() => showModal(record)}>编辑</Button>
          <Button icon={<DeleteOutlined />} size="small" danger onClick={() => handleDelete(record.id)}>删除</Button>
        </Space>
      ),
    },
  ];

  // 从localStorage加载列设置并应用到表格
  useEffect(() => {
    const storageKey = 'huifuAccountList_columnStates';
    const saved = localStorage.getItem(storageKey);
    let columnStates = {};
    
    if (saved) {
      try {
        columnStates = JSON.parse(saved);
      } catch (e) {
        console.error('Failed to parse column states:', e);
      }
    }
    
    const processedColumns = originalColumns.map(col => {
      const state = columnStates[col.key];
      if (state === 'hidden') {
        return null;
      }
      return col;
    }).filter(col => col !== null);
    
    setDisplayColumns(processedColumns);
  }, []);

  return (
    <div style={{ padding: '20px 0' }}>
      <PageTitle title="汇付账号管理" />
      
      <ListToolbar
        searchPlaceholder="搜索用户ID、真实姓名、手机号、汇付ID"
        searchValue={searchText}
        onSearchChange={setSearchText}
        onSearch={handleSearch}
        onReset={handleReset}
        onRefresh={handleRefresh}
        onExport={handleExport}
        showBatchDelete={false}
        showAdd={true}
        onAdd={() => showModal()}
        actions={[
          {
            key: 'download-template',
            label: '下载模板',
            icon: <DownloadOutlined />,
            onClick: handleDownloadTemplate,
            type: 'default',
          },
          {
            key: 'batch-import',
            label: '批量导入',
            icon: <UploadOutlined />,
            onClick: handleBatchImport,
            type: 'default',
          },
        ]}
      />

      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      <ResizableTable
        columns={displayColumns.length > 0 ? displayColumns : originalColumns}
        dataSource={accountData}
        rowKey="id"
        loading={loading}
        storageKey="huifuAccountList"
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showTotal: (total) => `共 ${total} 条数据`,
          pageSizeOptions: ['10', '20', '50', '100'],
        }}
        onChange={handleTableChange}
        scroll={{ x: 1500 }}
        locale={{ emptyText: '暂无汇付账号数据' }}
        bordered
        style={{ background: '#fff' }}
      />

      {/* 新增/编辑弹窗 */}
      <Modal
        title={editingAccount ? '编辑汇付账号' : '新增汇付账号'}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onOk={handleSubmit}
        width={600}
        destroyOnClose
        maskClosable={false}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="user_id"
            label="用户ID"
            rules={[{ required: !editingAccount, message: '请输入用户ID' }]}
          >
            <Select
              placeholder="选择用户"
              disabled={!!editingAccount}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={userList.map(user => ({
                value: user.id,
                label: `${user.nickname || '未命名'} (ID: ${user.id}, 手机: ${user.phone || '无'})`
              }))}
            />
          </Form.Item>
          <Form.Item
            name="real_name"
            label="真实姓名"
            rules={[{ required: true, message: '请输入真实姓名' }]}
          >
            <Input placeholder="请输入真实姓名" maxLength={50} />
          </Form.Item>
          <Form.Item
            name="phone"
            label="手机号"
          >
            <Input placeholder="请输入手机号" maxLength={20} />
          </Form.Item>
          <Form.Item
            name="id_card"
            label="身份证号"
          >
            <Input placeholder="请输入身份证号" maxLength={20} />
          </Form.Item>
          <Form.Item
            name="cert_validity_type"
            label="证件有效期类型"
            initialValue={0}
          >
            <Select>
              <Select.Option value={0}>非长期有效</Select.Option>
              <Select.Option value={1}>长期有效</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="cert_begin_date"
            label="证件有效期开始日期"
          >
            <Input placeholder="格式：yyyyMMdd，例如：20200101" maxLength={8} />
          </Form.Item>
          <Form.Item
            name="cert_end_date"
            label="证件有效期截止日期"
          >
            <Input placeholder="格式：yyyyMMdd，例如：20300101（长期有效时留空）" maxLength={8} />
          </Form.Item>
          <Form.Item
            name="huifu_id"
            label="汇付ID"
            rules={[{ required: true, message: '请输入汇付ID' }]}
          >
            <Input placeholder="请输入汇付ID（18位）" maxLength={18} />
          </Form.Item>
          <Form.Item
            name="status"
            label="状态"
            initialValue={1}
          >
            <Select>
              <Select.Option value={1}>正常</Select.Option>
              <Select.Option value={0}>禁用</Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default HuifuAccountList;

